/**
 * Unified Image URL Utilities
 *
 * THIS IS THE SINGLE SOURCE OF TRUTH for all image URL generation.
 * All components and the Vuex store should use these functions.
 *
 * Supports:
 * - Cloudflare Images (auto-resizing CDN)
 * - Cloudflare R2 CDN (static files)
 * - Legacy API endpoints (fallback)
 *
 * Usage:
 *   import { getProductImageUrl, getCategoryImageUrl } from '@/utils/imageUtils'
 *   const url = getProductImageUrl(productId, { variant: 'card' })
 *   const url = getCategoryImageUrl(category) // accepts id, name, or object
 */

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Get configuration values from window globals
 * These are set by the config.{school}.js files loaded at runtime
 * SSR-safe: returns defaults when window is undefined
 */
function getConfig() {
  if (typeof window === 'undefined') {
    return {
      cfImagesEnabled: false,
      cfAccountHash: '',
      cfDefaultVariant: 'card',
      cdnEnabled: false,
      cdnBaseUrl: '',
      schoolPath: '',
      apiImageBase: '',
      categoryImagePath: '/images/'
    }
  }

  return {
    cfImagesEnabled: window.VUE_APP_CF_IMAGES_ENABLED === true || window.VUE_APP_CF_IMAGES_ENABLED === 'true',
    cfAccountHash: window.VUE_APP_CF_IMAGES_ACCOUNT_HASH || '',
    cfDefaultVariant: window.VUE_APP_CF_IMAGES_DEFAULT_VARIANT || 'card',
    cfImagesVersion: window.VUE_APP_CF_IMAGES_VERSION || '',
    cfCategoryPrefix: window.CATEGORY_IMAGE_CF_PREFIX || '',
    cdnEnabled: window.VUE_APP_CDN_ENABLED === true || window.VUE_APP_CDN_ENABLED === 'true',
    cdnBaseUrl: window.VUE_APP_CDN_BASE_URL || '',
    schoolPath: window.VUE_APP_CDN_SCHOOL_PATH || '',
    apiImageBase: window.VUE_CONFIG_API_IMAGE || '',
    categoryImagePath: window.CATEGORY_IMAGE_PATH || '/images/'
  }
}

import logger from './logger'

// ============================================================================
// FALLBACK MAPPINGS
// ============================================================================

/**
 * Hardcoded product image mappings for products without imageFilename in DB
 * These are legacy mappings that should eventually be migrated to the database
 */
const PRODUCT_IMAGE_FALLBACKS = {
  2010609: 'P 118 Cobalt Blue Tumbler Wolfhead Circle  40 oz.jpg',
  2010610: 'P 119 White Tumbler Wolfhead  Circle  40 oz.jpg',
  2010666: 'P175 Bowl Soup 24 oz. Coblat Blue.jpg',
  2010757: 'P 118 Cobalt Blue Tumbler Wolfhead Circle  40 oz.jpg',
  2010801: 'P 310 Glass Tankyard 17.5 oz.jpg',
  2010802: 'P 311 Glass Frosted Wrap design 16 oz.jpg',
  2011017: 'P 526   Mug Etched Military  Green 16 oz.jpg',
  2011018: 'P527 Mug 16 oz Matte San.jpg',
  2011020: 'P 529 Mug  Black Alumni 11 oz.jpg',
  2011021: 'P 530  Mug Black Nursing 11 oz.jpg',
  2011034: 'P543 Ornament.jpg',
  2011233: 'P 742 Sport Bottle Bike Tritan  26 oz Blue.jpg',
  2011454: 'P963 Tumbler Seal White 16 oz.jpg',
  2011455: 'P 964 Insulated 18 oz Grey.jpg',
  2011456: 'P 965  Tumbler White Nursing  20 oz.jpg',
  2011457: 'P 966 Tumbler Blue  Dental Seal 20 oz.jpg',
  2011458: 'p 9949  Tumbler Vertical Logo 22 oz Grey.jpg',
  2011459: 'P968 Tumbler Beach Party 32 oz Blue.jpg',
  2011460: 'P9988 Tumbler Corkster 18 oz White.jpg',
  2011472: 'P 981 Water Bottle  Blue.jpg',
  2011473: 'P 982  Water Bottle SS 32 oz Blue.jpg',
  2011474: 'P 983 Water Bottle SS  Maroon 32 oz.jpg',
  2011612: 'P1121 Glass Candy Etched Jar 16oz.jpg',
  2011613: 'P 1122 Water Bottle Etched Grey w Handle 30 oz.jpg',
  2011614: 'P1123 Frame Pewter Vertical 4x6.jpg',
  2011615: 'p1124 Frame Pewter  Horizontal 4x6.jpg',
  2011626: 'P 1135 Tumbler 20 oz Black Wolf Logo.jpg',
  2011627: 'P 1136 Tumbler Maroon Wolf Logo 20 oz.jpg',
  2011628: 'P 1137 Tumbler 20 oz  Purple Wolf Logo.jpg'
}

/**
 * Legacy category ID to name mappings
 * Supports both old and new database category IDs
 */
const LEGACY_CATEGORY_ID_MAP = {
  // NEW SVDB_Westmoreland_dev database category IDs (622103005)
  118: 'uniforms & apparel',
  119: 'school supplies',
  120: 'technology & equipment',
  121: 'gift cards',
  122: 'featured items',
  123: 'course materials',
  124: 'miscellaneous',

  // OLD database category IDs for fallback compatibility
  112: 'uniforms & apparel',
  113: 'school supplies',
  114: 'technology & equipment',
  117: 'gift cards',

  // Legacy IDs for additional fallback
  1393: 'school supplies',
  1391: 'uniforms & apparel',
  1404: 'school supplies',
  1390: 'school supplies',
  1389: 'uniforms & apparel',
  1388: 'gift cards',
  1392: 'school supplies'
}

/**
 * Category name patterns to image filename mappings
 */
const CATEGORY_IMAGE_MAP = {
  'uniform': 'apparel.jpg',
  'apparel': 'apparel.jpg',
  'supplies': 'supplies.jpg',
  'technology': 'technology.jpg',
  'equipment': 'technology.jpg',
  'gift': 'gift-cards.jpg',
  'featured': 'featured.jpg',
  'merchandise': 'merchandise.jpg',
  'books': 'books.jpg',
  'course': 'course-materials.jpg'
}

const DEFAULT_CATEGORY_IMAGE = 'course-materials.jpg'
const DEFAULT_PRODUCT_IMAGE = '/images/newproduct.jpg'

// ============================================================================
// PRODUCT IMAGE FUNCTIONS
// ============================================================================

/**
 * Clean up a filename and fix common extension issues
 * @param {string} filename - Raw filename from database
 * @returns {string} Cleaned filename
 */
function cleanFilename(filename) {
  if (!filename) return null

  let clean = filename.trim()

  // Fix forward slash extension issues (e.g., "/jpg" -> ".jpg")
  clean = clean.replace(/\/jpg$/, '.jpg')
  clean = clean.replace(/\/j$/, '.jpg')
  clean = clean.replace(/\/jpeg$/, '.jpeg')
  clean = clean.replace(/\/png$/, '.png')

  // Fix truncated .jpg extension
  if (clean.endsWith('.j')) {
    clean = clean + 'pg'
  }

  // Add .jpg if no extension
  if (!clean.includes('.')) {
    clean = clean + '.jpg'
  }

  return clean
}

/**
 * Generate a legacy API image URL from a filename
 *
 * @param {string} filename - The image filename (e.g., 'product.jpg')
 * @param {string} apiBase - The API base URL (optional, uses config if not provided)
 * @returns {string} The legacy API image URL
 */
export function getLegacyImageUrl(filename, apiBase = null) {
  const config = getConfig()
  const imageApiBase = apiBase || config.apiImageBase || ''

  if (!filename || !imageApiBase) {
    return DEFAULT_PRODUCT_IMAGE
  }

  const cleanedFilename = cleanFilename(filename)
  if (!cleanedFilename) {
    return DEFAULT_PRODUCT_IMAGE
  }

  const lastDotIndex = cleanedFilename.lastIndexOf('.')
  if (lastDotIndex > 0) {
    const name = cleanedFilename.substring(0, lastDotIndex)
    const ext = cleanedFilename.substring(lastDotIndex + 1)
    return `${imageApiBase}getimage/${encodeURIComponent(name)}/${ext}`
  }

  return DEFAULT_PRODUCT_IMAGE
}

/**
 * Generate a product image URL
 *
 * Priority order:
 * 1. Cloudflare Images (if enabled) - auto-resizing CDN
 * 2. Cloudflare R2 CDN (if enabled) - static files
 * 3. Legacy API endpoint - getimage/{name}/{ext}
 * 4. Hardcoded fallback mappings
 * 5. Default placeholder
 *
 * @param {number|string} productId - The product ID
 * @param {Object} options - Optional configuration
 * @param {string} options.variant - Cloudflare Images variant ('thumb', 'card', 'detail', 'public')
 * @param {string} options.format - Image format for R2 CDN ('png', 'jpg', 'auto')
 * @param {Object} options.product - Optional product object with imageFilename/imageUrl
 * @returns {string} The image URL
 */
export function getProductImageUrl(productId, options = {}) {
  const config = getConfig()
  const { variant, format = 'auto', product } = options
  const selectedVariant = variant || config.cfDefaultVariant

  // Check for direct imageUrl on product (from API)
  if (product?.imageUrl) {
    logger.debug(`Using product.imageUrl for ${productId}:`, product.imageUrl)
    return product.imageUrl
  }

  // Priority 1: Cloudflare Images (auto-resizing)
  if (config.cfImagesEnabled && config.cfAccountHash) {
    try {
      let imageId
      if (product?.imageFilename && product.imageFilename.startsWith('product-')) {
        // Use the stored imageFilename (may point to a shared image)
        const baseFilename = product.imageFilename.replace(/\.(png|jpg|jpeg|gif|webp)$/i, '')
        imageId = config.schoolPath ? `${config.schoolPath}-${baseFilename}` : baseFilename
      } else {
        // Construct from productId
        imageId = config.schoolPath ? `${config.schoolPath}-product-${productId}` : `product-${productId}`
      }

      const qs = config.cfImagesVersion ? `?v=${config.cfImagesVersion}` : ''
      const url = `https://imagedelivery.net/${config.cfAccountHash}/${imageId}/${selectedVariant}${qs}`
      logger.debug(`Cloudflare Images URL for ${productId}:`, url)
      return url
    } catch (error) {
      logger.warn(`Cloudflare Images failed for product ${productId}, trying R2 fallback`, error)
    }
  }

  // Priority 2: Cloudflare R2 CDN (static files)
  if (config.cdnEnabled && config.cdnBaseUrl && !config.cdnBaseUrl.includes('XXXXXXXX')) {
    try {
      const cdnFormat = format === 'auto' ? 'png' : format
      const schoolPrefix = config.schoolPath ? `${config.schoolPath}/` : ''
      const url = `${config.cdnBaseUrl}/${schoolPrefix}product-${productId}.${cdnFormat}`
      logger.debug(`R2 CDN URL for ${productId}:`, url)
      return url
    } catch (error) {
      logger.warn(`CDN URL construction failed for product ${productId}, using legacy fallback`, error)
    }
  }

  // Priority 3: Legacy API with product's imageFilename
  let filename = product?.imageFilename || null

  // Priority 4: Hardcoded fallback mappings
  if (!filename) {
    filename = PRODUCT_IMAGE_FALLBACKS[productId]
  }

  if (filename && config.apiImageBase) {
    return getLegacyImageUrl(filename, config.apiImageBase)
  }

  // Priority 5: Default placeholder
  return DEFAULT_PRODUCT_IMAGE
}

// ============================================================================
// CATEGORY IMAGE FUNCTIONS
// ============================================================================

/**
 * Resolve a category to its name string
 * @param {number|string|Object} categoryIdOrName - Category ID, name, or object
 * @param {Array} categories - Optional categories array to look up IDs
 * @returns {string} Category name (lowercase)
 */
function resolveCategoryName(categoryIdOrName, categories = []) {
  if (typeof categoryIdOrName === 'object' && categoryIdOrName?.name) {
    return categoryIdOrName.name.toLowerCase()
  }

  if (typeof categoryIdOrName === 'string') {
    return categoryIdOrName.toLowerCase()
  }

  if (typeof categoryIdOrName === 'number') {
    // Try to find in categories array first
    const category = categories.find(c => c.id === categoryIdOrName)
    if (category?.name) {
      return category.name.toLowerCase()
    }
    // Fall back to legacy ID mapping
    return (LEGACY_CATEGORY_ID_MAP[categoryIdOrName] || '').toLowerCase()
  }

  return ''
}

/**
 * Map a category name to an image filename
 * @param {string} categoryName - Category name (lowercase)
 * @returns {string} Image filename
 */
function getCategoryFilename(categoryName) {
  if (!categoryName) return DEFAULT_CATEGORY_IMAGE

  // Check each pattern in the map
  for (const [pattern, filename] of Object.entries(CATEGORY_IMAGE_MAP)) {
    if (categoryName.includes(pattern)) {
      return filename
    }
  }

  return DEFAULT_CATEGORY_IMAGE
}

/**
 * Generate a category image URL
 *
 * Priority order:
 * 1. Category object's imageUrl property (from API)
 * 2. Category object's thumbnailUrl property (from API)
 * 3. Cloudflare Images (if enabled and cfImageId provided)
 * 4. Static assets in /images/ folder based on category name
 *
 * @param {number|string|Object} categoryIdOrName - Category ID, name, or object
 * @param {Object} options - Optional configuration
 * @param {string} options.variant - Cloudflare Images variant
 * @param {string} options.cfImageId - Optional Cloudflare Images ID
 * @param {Array} options.categories - Optional categories array for ID lookup
 * @returns {string} The category image URL
 */
export function getCategoryImageUrl(categoryIdOrName, options = {}) {
  const config = getConfig()
  const { variant, cfImageId, categories = [] } = options
  const selectedVariant = variant || config.cfDefaultVariant

  // Resolve the category OBJECT regardless of how we were called — the object itself, a numeric id,
  // or a name/slug. Callers differ: LandingPage passes category.id (number); others pass the object.
  // The API shape uses categoryId/categorySlug/categoryName (not id/slug/name), so match both, and
  // compare ids as strings (the API returns categoryId as a string). This is the fix for admin category
  // uploads not showing: previously the id path only read imageUrl/thumbnailUrl, never imageFilename.
  let catObj = null
  if (categoryIdOrName && typeof categoryIdOrName === 'object') {
    catObj = categoryIdOrName
  } else if (categories.length > 0) {
    const key = categoryIdOrName
    const ks = String(key)
    catObj = categories.find(c =>
      String(c.id) === ks || String(c.categoryId) === ks ||
      c.name === key || c.categoryName === key || c.slug === key || c.categorySlug === key
    ) || null
  }

  // Priority 0: a fresh admin upload wins. UploadCategoryImage writes a 'category-<id>-<ticks>'
  // imageFilename; build the CF id like products do ({schoolPath}-{imageFilename}). This must take
  // precedence over a legacy imageUrl (seeded fundingportalstorage.blob) so admin uploads display.
  if (catObj && catObj.imageFilename && catObj.imageFilename.startsWith('category-') &&
      config.cfImagesEnabled && config.cfAccountHash) {
    const base = catObj.imageFilename.replace(/\.(png|jpg|jpeg|gif|webp)$/i, '')
    const imageId = config.schoolPath ? `${config.schoolPath}-${base}` : base
    const qs = config.cfImagesVersion ? `?v=${config.cfImagesVersion}` : ''
    return `https://imagedelivery.net/${config.cfAccountHash}/${imageId}/${selectedVariant}${qs}`
  }

  // Priority 1: explicit legacy imageUrl / thumbnailUrl on the resolved category
  if (catObj) {
    if (catObj.imageUrl) {
      logger.debug(`Using category.imageUrl:`, catObj.imageUrl)
      return catObj.imageUrl
    }
    if (catObj.thumbnailUrl) {
      logger.debug(`Using category.thumbnailUrl:`, catObj.thumbnailUrl)
      return catObj.thumbnailUrl
    }
  }

  // Priority 2: Cloudflare Images (if cfImageId provided explicitly)
  if (cfImageId && config.cfImagesEnabled && config.cfAccountHash) {
    const url = `https://imagedelivery.net/${config.cfAccountHash}/${cfImageId}/${selectedVariant}`
    logger.debug(`Cloudflare Images URL for category:`, url)
    return url
  }

  // Priority 3: Cloudflare Images via tenant category prefix (e.g. 'westmoreland-category-')
  const categoryName = resolveCategoryName(categoryIdOrName, categories)
  if (config.cfImagesEnabled && config.cfAccountHash && config.cfCategoryPrefix) {
    const slug = getCategoryFilename(categoryName).replace(/\.(jpg|jpeg|png|webp)$/i, '')
    const imageId = config.cfCategoryPrefix + slug
    const url = `https://imagedelivery.net/${config.cfAccountHash}/${imageId}/${selectedVariant}`
    logger.debug(`CF Images category prefix URL for "${categoryName}":`, url)
    return url
  }

  // Priority 4: Static asset based on category name
  const filename = getCategoryFilename(categoryName)
  const url = `${config.categoryImagePath}${filename}`

  logger.debug(`Category "${categoryIdOrName}" → "${categoryName}" → ${url}`)
  return url
}

// ============================================================================
// HERO IMAGE FUNCTIONS
// ============================================================================

/**
 * Get the hero background image URL
 *
 * @param {Object} options - Optional configuration
 * @param {string} options.variant - Cloudflare Images variant
 * @returns {string} The hero background image URL
 */
export function getHeroBackgroundUrl(options = {}) {
  // Check for school-specific hero image in window config
  if (typeof window !== 'undefined' && window.HERO_BACKGROUND_IMAGE) {
    return window.HERO_BACKGROUND_IMAGE
  }

  const config = getConfig()
  const { variant = 'hero' } = options

  // Use Cloudflare Images if available
  if (config.cfImagesEnabled && config.cfAccountHash && config.schoolPath) {
    return `https://imagedelivery.net/${config.cfAccountHash}/${config.schoolPath}-hero/${variant}`
  }

  // Fallback to static asset
  return '/images/hero-background.jpg'
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if Cloudflare Images is enabled and configured
 * @returns {boolean} True if Cloudflare Images can be used
 */
export function isCloudflareImagesEnabled() {
  const config = getConfig()
  return config.cfImagesEnabled && !!config.cfAccountHash
}

/**
 * Check if R2 CDN is enabled and configured
 * @returns {boolean} True if R2 CDN can be used
 */
export function isR2CdnEnabled() {
  const config = getConfig()
  return config.cdnEnabled && !!config.cdnBaseUrl && !config.cdnBaseUrl.includes('XXXXXXXX')
}

/**
 * Get the fallback product image mappings (for debugging/admin)
 * @returns {Object} The product ID to filename mappings
 */
export function getProductFallbackMappings() {
  return { ...PRODUCT_IMAGE_FALLBACKS }
}

/**
 * Get the legacy category ID mappings (for debugging/admin)
 * @returns {Object} The category ID to name mappings
 */
export function getLegacyCategoryIdMappings() {
  return { ...LEGACY_CATEGORY_ID_MAP }
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  getProductImageUrl,
  getLegacyImageUrl,
  getCategoryImageUrl,
  getHeroBackgroundUrl,
  isCloudflareImagesEnabled,
  isR2CdnEnabled,
  getProductFallbackMappings,
  getLegacyCategoryIdMappings
}
